import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { EdgeRectangleShape } from '@sorare/core/src/atoms/ui/EdgeRectangleShape';

import { RewardConfigImage } from './RewardConfigImage';
import { RewardConfigText } from './RewardConfigText';
import type { NewAnyRewardConfig_anyRewardConfigInterface } from './__generated__/index.graphql';
import { getAnyRewardConfigColors } from './utils';

export enum Variant {
  TEXT = 'TEXT',
  INLINE = 'INLINE',
  CHIP = 'CHIP',
}

const StyledHorizontal = styled(Horizontal)`
  padding: 1px 2px;
`;
const StyledVertical = styled(Vertical).attrs({ center: true, gap: 0 })`
  & > *:nth-child(2) {
    position: relative;
    margin-top: calc(-1 * var(--unit));
  }
`;

type Props = {
  anyRewardConfig: NewAnyRewardConfig_anyRewardConfigInterface;
  rarity?: Rarity | 'mix';
  variant?: Variant;
  afterText?: string;
  hideText?: boolean;
};

export const NewAnyRewardConfig = ({
  anyRewardConfig,
  variant = Variant.CHIP,
  rarity,
  afterText,
  hideText = false,
}: Props) => {
  if (variant === Variant.TEXT) {
    return (
      <Horizontal gap={0.5}>
        <RewardConfigImage
          anyRewardConfig={anyRewardConfig}
          rarity={rarity}
          size={3}
        />
        {!hideText && (
          <BrandXS color="var(--c-white)">
            <RewardConfigText anyRewardConfig={anyRewardConfig} />
            {afterText}
          </BrandXS>
        )}
      </Horizontal>
    );
  }

  const { bg, border } = getAnyRewardConfigColors({
    anyRewardConfig,
    rarity,
  });

  const anyRewardConfigRarity =
    'rarity' in anyRewardConfig ? anyRewardConfig.rarity : rarity;

  if (variant === Variant.INLINE) {
    return (
      <EdgeRectangleShape
        border={border}
        background={bg}
        padding="2px var(--unit)"
        offset="8px"
      >
        <StyledHorizontal gap={0.5}>
          <RewardConfigImage
            anyRewardConfig={anyRewardConfig}
            rarity={anyRewardConfigRarity}
            size={2}
          />
          {!hideText && (
            <BrandXS color="var(--c-white)">
              <RewardConfigText anyRewardConfig={anyRewardConfig} />
              {afterText}
            </BrandXS>
          )}
        </StyledHorizontal>
      </EdgeRectangleShape>
    );
  }

  return (
    <StyledVertical>
      <EdgeRectangleShape
        border={border}
        background={bg}
        padding="7px var(--intermediate-unit)"
        offset="12px"
      >
        <RewardConfigImage
          anyRewardConfig={anyRewardConfig}
          rarity={anyRewardConfigRarity}
          size={3}
        />
      </EdgeRectangleShape>
      {!hideText && (
        <BrandS>
          <RewardConfigText anyRewardConfig={anyRewardConfig} />
          {afterText}
        </BrandS>
      )}
    </StyledVertical>
  );
};

NewAnyRewardConfig.fragments = {
  anyRewardConfigInterface: gql`
    fragment NewAnyRewardConfig_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...RewardConfigImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
      ...getAnyRewardConfigColors_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${getAnyRewardConfigColors.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<NewAnyRewardConfig_anyRewardConfigInterface>,
};
